import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { tap } from 'rxjs';

import { TextareaComponent } from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';

import { DiscussionFormalQuestion, DiscussionModel, DiscussionType } from '../../../models/discussion.models';
import { DiscussionCustomQuestionFormValue, DiscussionFormMode } from '../../../models/form/discussion-form.model';
import { DiscussionFormBase } from '../discussion-form-base/discussion-form-base';

@Component({
  selector: 'nty-custom-question-form',
  imports: [ReactiveFormsModule, UserNamePipe, TextareaComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DiscussionCustomQuestionFormComponent), multi: true },
  ],
  templateUrl: 'discussion-custom-question.component.html',
  styles: `
    ion-list {
      background: transparent;
    }
    ion-item {
      --background: transparent;
    }
  `,
  styleUrls: ['../../discussion-form-container/common.scss'],
})
export class DiscussionCustomQuestionFormComponent extends DiscussionFormBase implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) discussion!: DiscussionModel;
  @Input() mode: DiscussionFormMode = DiscussionFormMode.Meeting;
  @Input({ required: true }) questions!: DiscussionFormalQuestion[];

  form = new FormGroup({
    managerQuestions: new FormArray<FormControl<string>>([]),
    manageeQuestions: new FormArray<FormControl<string>>([]),
  });

  get questionCtrls() {
    return this.mode === DiscussionFormMode.Manager
      ? this.form.controls.managerQuestions.controls
      : this.form.controls.manageeQuestions.controls;
  }

  get sectionTitle() {
    return this.discussion.type === DiscussionType.quarterly ? 'Quarterly Questions' : 'Annual Questions';
  }

  get customQuestions() {
    return this.mode === DiscussionFormMode.Manager
      ? (this.form.value.managerQuestions ?? [])
      : (this.form.value.manageeQuestions ?? []);
  }

  ngOnInit() {
    this.questions.forEach(q => {
      this.form.controls.managerQuestions.push(new FormControl(q.managerAnswer, { nonNullable: true }));
      this.form.controls.manageeQuestions.push(new FormControl(q.manageeAnswer, { nonNullable: true }));
    });

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          const transformed: DiscussionCustomQuestionFormValue = [];
          const questions = this.customQuestions;

          for (let i = 0; i < questions.length; i++) {
            const managerAnswer = this.form.value.managerQuestions?.[i] ?? '';
            const manageeAnswer = this.form.value.manageeQuestions?.[i] ?? '';
            transformed.push({
              manager: { answer: managerAnswer },
              managee: { answer: manageeAnswer },
            });
          }

          if (this.onChange) this.onChange(transformed);
          if (this.onTouched) this.onTouched();
        })
      )
      .subscribe();
  }

  writeValue(val: DiscussionCustomQuestionFormValue): void {
    this.form.reset();
    const managerAnswers = val.map(val => val.manager.answer);
    const manageeAnswers = val.map(val => val.managee.answer);
    this.form.patchValue(
      {
        managerQuestions: managerAnswers,
        manageeQuestions: manageeAnswers,
      },
      { emitEvent: false }
    );
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    if (this.mode === DiscussionFormMode.Manager) {
      this.form.controls.manageeQuestions.disable();
    }

    if (this.mode === DiscussionFormMode.Managee) {
      this.form.controls.managerQuestions.disable();
    }
  }
}
